import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faSpinner, faSearch,
    faFilter, faPlus,
    faExclamationTriangle,
    faStore, faCheckCircle, faTimesCircle,
} from '@fortawesome/free-solid-svg-icons'
import PageHeader from '../../utils/PageHeader'
import { API_URL } from '../../utils/Constants/URLS'
import '../../../css/purchase.css'
import '../../../css/common.css'

/* ─── GST Badge ────────────────────────────────────────────── */
const GSTBadge = ({ isGSTRegistered }) => {
    if (isGSTRegistered) {
        return (
            <span className='inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-50 border border-green-200 text-green-700 whitespace-nowrap'>
                <FontAwesomeIcon icon={faCheckCircle} className='text-xs' />
                GST Registered
            </span>
        )
    }
    return (
        <span className='inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-50 border border-red-200 text-red-700 whitespace-nowrap'>
            <FontAwesomeIcon icon={faTimesCircle} className='text-xs' />
            Non-GST
        </span>
    )
}

/* ══════════════════════════════════════════
   ViewVendors — Vendor List
══════════════════════════════════════════ */
const ViewVendors = () => {
    const token = useSelector((store) => store.user?.token) || localStorage.getItem('token')
    const navigate = useNavigate()

    const [vendorList, setVendorList] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [search, setSearch] = useState('')
    const [gstFilter, setGstFilter] = useState('all')
    const [page, setPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [totalDocs, setTotalDocs] = useState(0)

    const LIMIT = 15

    /* ── Fetch from API ── */
    const fetchVendors = useCallback(
        async (pg = 1) => {
            setLoading(true)
            setError('')
            try {
                const params = new URLSearchParams({
                    page: pg,
                    limit: LIMIT,
                    sortBy: 'createdAt',
                    sortOrder: 'desc',
                })
                if (search.trim()) params.append('search', search.trim())
                if (gstFilter !== 'all') params.append('isGSTRegistered', gstFilter)

                const response = await fetch(`${API_URL}vendors?${params.toString()}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    }
                })
                const data = await response.json()

                const payload = data?.data ?? data
                const docs = payload?.docs ?? payload?.vendors ?? (Array.isArray(payload) ? payload : [])
                const pages = payload?.totalPages ?? payload?.pages ?? 1
                const total = payload?.totalDocs ?? payload?.total ?? docs.length

                setVendorList(Array.isArray(docs) ? docs : [])
                setTotalPages(pages)
                setTotalDocs(total)
                setPage(pg)
            } catch (e) {
                console.error(e)
                setError(e.message || 'Failed to load vendors.')
                setVendorList([])
            } finally {
                setLoading(false)
            }
        },
        [token, search, gstFilter]
    )

    /* Initial load */
    useEffect(() => { fetchVendors(1) }, []) // eslint-disable-line react-hooks/exhaustive-deps

    /* Re-fetch when filter changes */
    useEffect(() => { fetchVendors(1) }, [gstFilter]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleSearch = (e) => { e.preventDefault(); fetchVendors(1) }

    return (
        <div>
            <PageHeader
                pageName='VIEW VENDORS'
                breadcrump1='Home'
                breadcrump2='View Vendors'
                link='/dashboard'
            />

            <div className='main lg:p-6 xl:p-6 2xl:p-6 sm:p-3 md:p-3'>

                {/* ── Top action ── */}
                <div className='flex justify-end mb-4'>
                    <button
                        className='btn add-btn text-white flex items-center gap-2 font-bold text-sm'
                        onClick={() => navigate('/purchase/add_vendor')}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        Add Vendor
                    </button>
                </div>

                {/* ── Filter bar ── */}
                <div className='bg-white border border-gray-200 rounded-xl p-4 mb-5 shadow-sm'>
                    <div className='flex items-center gap-3 flex-wrap'>
                        {/* Search */}
                        <form onSubmit={handleSearch} className='flex-1 min-w-56 relative'>
                            <FontAwesomeIcon
                                icon={faSearch}
                                className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none'
                            />
                            <input
                                type='text'
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder='Search by vendor name or email…'
                                className='purchase-search-input'
                            />
                        </form>

                        {/* GST Filter */}
                        <div className='flex items-center gap-2'>
                            <FontAwesomeIcon icon={faFilter} className='text-gray-400 text-sm' />
                            <select
                                className='form-select form-select-sm'
                                value={gstFilter}
                                onChange={(e) => setGstFilter(e.target.value)}
                            >
                                <option value='all'>All Vendors</option>
                                <option value='true'>GST Registered</option>
                                <option value='false'>Non-GST</option>
                            </select>
                        </div>

                        <button
                            className='btn filter-btn text-white btn-sm px-4'
                            onClick={() => fetchVendors(1)}
                        >
                            Apply
                        </button>
                    </div>
                </div>

                {/* ── Error banner ── */}
                {error && (
                    <div className='flex items-center gap-2 rounded-lg px-4 py-3 mb-4 bg-red-50 border border-red-200 text-red-700 text-sm'>
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        {error}
                    </div>
                )}

                {/* ── Table card ── */}
                <div className='bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden'>
                    <div className='px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-2'>
                        <div className='flex items-center gap-2'>
                            <FontAwesomeIcon icon={faStore} className='text-purple-600' />
                            <h6 className='font-bold text-gray-800 mb-0'>Vendors</h6>
                        </div>
                        {!loading && totalDocs > 0 && (
                            <span className='text-xs text-gray-500 font-semibold'>
                                {totalDocs} record{totalDocs !== 1 ? 's' : ''}
                            </span>
                        )}
                    </div>

                    {/* Loading */}
                    {loading ? (
                        <div className='text-center py-14 text-gray-400'>
                            <FontAwesomeIcon icon={faSpinner} spin className='text-3xl mb-3' />
                            <p className='text-sm'>Loading vendors…</p>
                        </div>
                    ) : vendorList.length === 0 ? (
                        <div className='text-center py-14 text-gray-400'>
                            <FontAwesomeIcon icon={faStore} className='text-4xl mb-3 text-gray-300' />
                            <p className='text-sm font-semibold text-gray-500'>No Vendors Found</p>
                            <p className='text-xs text-gray-400'>No vendors match your current filters.</p>
                        </div>
                    ) : (
                        <div className='table-responsive'>
                            <table className='table align-middle table-nowrap mb-0 purchase-table'>
                                <thead className='table-light'>
                                    <tr>
                                        <th>#</th>
                                        <th>Vendor Name</th>
                                        <th>Email</th>
                                        <th>Phone</th>
                                        <th>GSTIN</th>
                                        <th>Address</th>
                                        <th>Contact Persons</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {vendorList.map((vendor, idx) => (
                                        <tr key={vendor._id || idx}>
                                            <td className='text-sm text-gray-500'>{idx + 1}</td>

                                            <td>
                                                <span className='font-bold text-purple-700 text-sm'>
                                                    {vendor.vendorName || '—'}
                                                </span>
                                            </td>

                                            <td className='text-sm text-gray-700'>
                                                {vendor.vendorEmail || '—'}
                                            </td>
                                            <td className='text-sm text-gray-700'>
                                                {vendor.vendorPhone || '—'}
                                            </td>
                                            <td className='text-sm text-gray-700'>
                                                {vendor.gstin || '—'}
                                            </td>
                                            <td className='text-sm text-gray-700'>
                                                {Array.isArray(vendor.address) && vendor.address.length > 0
                                                    ? vendor.address.map((addr, i) => (
                                                        <div key={i} className={i > 0 ? 'mt-2 pt-2 border-top' : ''}>
                                                            {addr.addressNickName && (
                                                                <span className='badge bg-secondary me-1 mb-1' style={{ fontSize: '0.7rem' }}>
                                                                    {addr.addressNickName}
                                                                </span>
                                                            )}
                                                            <div>
                                                                {[
                                                                    addr.addressLine1,
                                                                    addr.addressLine2,
                                                                    addr.state,
                                                                    addr.country,
                                                                    addr.pincode,
                                                                ].filter(Boolean).join(', ')}
                                                            </div>
                                                        </div>
                                                    ))
                                                    : '—'}
                                            </td>
                                            <td className='text-sm text-gray-700'>
                                                {vendor.contactPersons && vendor.contactPersons.length > 0
                                                    ? vendor.contactPersons.map((cp, i) => (
                                                        <div key={i}>
                                                            <span className='font-semibold'>{cp.name}</span>
                                                            {cp.designation ? ` — ${cp.designation}` : ''}
                                                            {cp.phone ? ` (${cp.phone})` : ''}
                                                        </div>
                                                    ))
                                                    : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* ── Pagination ── */}
                    {totalPages > 1 && (
                        <div className='px-5 py-3 border-t border-gray-100 flex justify-end gap-2'>
                            <button
                                className='btn btn-sm btn-light border'
                                disabled={page <= 1}
                                onClick={() => fetchVendors(page - 1)}
                            >
                                « Prev
                            </button>
                            <span className='flex items-center text-sm text-gray-500 px-3'>
                                Page {page} of {totalPages}
                            </span>
                            <button
                                className='btn btn-sm btn-light border'
                                disabled={page >= totalPages}
                                onClick={() => fetchVendors(page + 1)}
                            >
                                Next »
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default ViewVendors
